/**
 * IDF Builder — Geometry Engine
 * 
 * Pure functions for building geometry: footprints, surfaces,
 * partitions, azimuths, compass binning, and snapping.
 * All coordinates in meters, building-local system.
 */

/**
 * Remove collinear (redundant midpoint) vertices from a 2-D polygon.
 *
 * EnergyPlus applies its own collinearity filter internally.  When two
 * footprints have the same raw vertex count but different numbers of
 * collinear points, EnergyPlus ends up with mismatched counts on paired
 * interzone surfaces → fatal "Vertex size mismatch" error.
 *
 * Pre-removing collinear vertices here ensures our IDF vertex count already
 * matches what EnergyPlus would compute, so the pairInterzoneFloors vertex-
 * count check compares the correct final values.
 *
 * @param {Array<{x:number,y:number}>} polygon - Input 2-D polygon (CCW)
 * @param {number} [toleranceSin=0.002] ≈ sin(0.1°) — matches EnergyPlus threshold
 * @returns {Array<{x:number,y:number}>} Simplified polygon (≥3 vertices)
 */
export function removeCollinearVertices(polygon, toleranceSin = 0.002) {
  if (!polygon || polygon.length <= 3) return polygon;

  let pts = [...polygon];
  let changed = true;

  // Iterate until no more collinear vertices can be removed
  while (changed && pts.length > 3) {
    changed = false;
    const m = pts.length;
    const kept = [];

    for (let i = 0; i < m; i++) {
      const prev = pts[(i - 1 + m) % m];
      const curr = pts[i];
      const next = pts[(i + 1) % m];

      const dx1 = curr.x - prev.x,  dy1 = curr.y - prev.y;
      const dx2 = next.x - curr.x,  dy2 = next.y - curr.y;
      const cross = Math.abs(dx1 * dy2 - dy1 * dx2);
      const len1  = Math.sqrt(dx1 * dx1 + dy1 * dy1);
      const len2  = Math.sqrt(dx2 * dx2 + dy2 * dy2);

      // Treat zero-length edges as collinear (duplicate vertices)
      const isCollinear = len1 < 1e-9 || len2 < 1e-9 ||
                          (cross / (len1 * len2)) < toleranceSin;

      if (isCollinear) {
        changed = true; // will loop again to cascade-remove
      } else {
        kept.push(curr);
      }
    }

    if (kept.length >= 3) pts = kept;
    else break; // Don't degenerate below a triangle
  }

  return pts;
}

/**
 * Convert rectangle params to a CCW footprint (viewed from above)
 * @param {number} x - Left edge X
 * @param {number} y - Bottom edge Y
 * @param {number} width - Width (X direction)
 * @param {number} depth - Depth (Y direction)
 * @returns {Array<{x: number, y: number}>} CCW polygon
 */
export function rectToFootprint(x, y, width, depth) {
  return [
    { x, y },
    { x: x + width, y },
    { x: x + width, y: y + depth },
    { x, y: y + depth },
  ];
}

/**
 * Generate 3D surfaces (walls, floor, ceiling) from a zone footprint
 * @param {Array<{x: number, y: number}>} footprint - CCW 2D polygon
 * @param {number} elevation - Z of floor slab
 * @param {number} height - Floor-to-ceiling height
 * @param {string} zoneId - Parent zone ID
 * @param {string} zoneName - Parent zone name
 * @returns {{ walls: Array, floor: Object, ceiling: Object }}
 */
export function footprintToSurfaces(footprint, elevation, height, zoneId, zoneName) {
  const n = footprint.length;
  const walls = [];
  const topZ = elevation + height;

  // Generate walls — each wall goes from footprint[i] to footprint[(i+1)%n]
  // EnergyPlus wall vertices: looking at wall from OUTSIDE, vertices go CCW
  // For an external wall, that means: top-left, top-right, bottom-right, bottom-left
  // (when viewed from outside the building)
  for (let i = 0; i < n; i++) {
    const p1 = footprint[i];
    const p2 = footprint[(i + 1) % n];

    // Wall vertices: viewed from outside, CCW winding
    // The outward normal for a CCW footprint wall segment (p1→p2) points outward
    // Vertices for EnergyPlus: upper-left, upper-right, lower-right, lower-left
    // (from outside view: p1 is left, p2 is right)
    const vertices = [
      { x: p1.x, y: p1.y, z: topZ },    // upper-left
      { x: p2.x, y: p2.y, z: topZ },    // upper-right
      { x: p2.x, y: p2.y, z: elevation }, // lower-right
      { x: p1.x, y: p1.y, z: elevation }, // lower-left
    ];

    const wallName = `${zoneName}:Wall${i + 1}`;
    const azimuth = computeWallAzimuth(p1, p2);

    walls.push({
      id: `${zoneId}_wall_${i}`,
      name: wallName,
      zone_id: zoneId,
      type: 'wall',
      azimuth,
      bin: bin8(azimuth),
      vertices,
      area: wallArea(p1, p2, height),
      boundary_condition: 'Outdoors', // default; overridden for partitions
      adjacent_surface: null,
      construction: null,
      openings: [],
    });
  }

  // Floor slab — outward normal must point DOWN (tilt ≈ 180°).
  // EnergyPlus UpperLeftCorner/Counterclockwise convention: vertices are CCW when
  // viewed from OUTSIDE the surface.  "Outside" for a floor = below it.
  // CCW from below = CW from above = REVERSED footprint order.
  const floorVertices = [...footprint].reverse().map((p) => ({ x: p.x, y: p.y, z: elevation }));
  const floor = {
    id: `${zoneId}_floor`,
    name: `${zoneName}:Floor`,
    zone_id: zoneId,
    type: 'floor',
    azimuth: null,
    bin: null,
    vertices: floorVertices,
    area: polygonArea(footprint),
    boundary_condition: 'Ground',
    adjacent_surface: null,
    construction: null,
    openings: [],
  };

  // Ceiling/roof — outward normal must point UP (tilt ≈ 0°).
  // "Outside" for a ceiling = above it = looking DOWN from above.
  // CCW from above = SAME as footprint order (not reversed).
  const ceilingVertices = footprint.map((p) => ({ x: p.x, y: p.y, z: topZ }));
  const ceiling = {
    id: `${zoneId}_ceiling`,
    name: `${zoneName}:Ceiling`,
    zone_id: zoneId,
    type: 'ceiling',
    azimuth: null,
    bin: null,
    vertices: ceilingVertices,
    area: polygonArea(footprint),
    boundary_condition: 'Outdoors', // top floor = roof
    adjacent_surface: null,
    construction: null,
    openings: [],
  };

  return { walls, floor, ceiling };
}

/**
 * Compute wall azimuth from two footprint points.
 * Azimuth = compass bearing of the OUTWARD normal.
 * For a CCW footprint segment p1→p2, the outward normal points right of the direction.
 * 
 * @param {{x: number, y: number}} p1 - Start point
 * @param {{x: number, y: number}} p2 - End point
 * @returns {number} Azimuth in degrees (0-360, 0=North, 90=East)
 */
export function computeWallAzimuth(p1, p2) {
  const dx = p2.x - p1.x;
  const dy = p2.y - p1.y;

  // Footprints are stored in CANVAS (screen) coordinates: +X = East (right),
  // +Y = SOUTH (down the screen). North is therefore −Y (top of screen), which
  // matches the "N" indicator the 2D editor draws at the top.
  //
  // Outward normal of a footprint segment p1→p2 (right-perpendicular) = (dy, −dx).
  //   East component  = normal.x          = dy
  //   North component = −normal.y = −(−dx) = dx   (North = −Y in canvas)
  //   compass bearing = atan2(East, North) = atan2(dy, dx)
  //
  // Verification (rectangular zone [TL,TR,BR,BL] in canvas y-down):
  //   top wall    TL→TR: dx>0, dy=0 → atan2(0, +) =   0° (North) ✓  ← top of screen = North
  //   right wall  TR→BR: dx=0, dy>0 → atan2(+, 0) =  90° (East)  ✓
  //   bottom wall BR→BL: dx<0, dy=0 → atan2(0, −) = 180° (South) ✓
  //   left wall   BL→TL: dx=0, dy<0 → atan2(−, 0) = 270° (West)  ✓
  const nx = dy;       // east component
  const ny = dx;       // north component (canvas North = −Y, so = −(−dx) = dx)
  let azimuth = Math.atan2(nx, ny) * (180 / Math.PI);
  if (azimuth < 0) azimuth += 360;

  return Math.round(azimuth * 100) / 100;
}

/**
 * Apply building rotation to a wall azimuth
 * @param {number} azimuth - Wall azimuth in building-local coords
 * @param {number} northAxis - Building rotation (degrees clockwise)
 * @returns {number} True azimuth (0-360)
 */
export function applyBuildingRotation(azimuth, northAxis) {
  let trueAzimuth = (azimuth + northAxis) % 360;
  if (trueAzimuth < 0) trueAzimuth += 360;
  return trueAzimuth;
}

/**
 * Bin a true azimuth into one of 8 compass directions.
 * From spec Section 5.3.
 * 
 * @param {number} trueAzimuth - Azimuth in degrees (0-360)
 * @returns {string} One of 'N','NE','E','SE','S','SW','W','NW'
 */
export function bin8(trueAzimuth) {
  const bins = ['N', 'NE', 'E', 'SE', 'S', 'SW', 'W', 'NW'];
  return bins[Math.floor(((trueAzimuth + 22.5) % 360) / 45)];
}

/**
 * Bin a true azimuth into 4 cardinal directions (for ECSBC).
 * @param {number} trueAzimuth - Azimuth in degrees (0-360)
 * @returns {string} One of 'N','E','S','W'
 */
export function bin4(trueAzimuth) {
  const bins = ['N', 'E', 'S', 'W'];
  return bins[Math.floor(((trueAzimuth + 45) % 360) / 90)];
}

/**
 * Compute wall area from two footprint points and height
 */
export function wallArea(p1, p2, height) {
  const dx = p2.x - p1.x;
  const dy = p2.y - p1.y;
  const length = Math.sqrt(dx * dx + dy * dy);
  return length * height;
}

/**
 * Compute area of a 2D polygon (Shoelace formula)
 * @param {Array<{x: number, y: number}>} polygon - CCW polygon
 * @returns {number} Positive area
 */
export function polygonArea(polygon) {
  let area = 0;
  const n = polygon.length;
  for (let i = 0; i < n; i++) {
    const j = (i + 1) % n;
    area += polygon[i].x * polygon[j].y;
    area -= polygon[j].x * polygon[i].y;
  }
  return Math.abs(area / 2);
}

/**
 * Compute the distance between two 2D points
 */
export function distance2D(p1, p2) {
  return Math.sqrt((p2.x - p1.x) ** 2 + (p2.y - p1.y) ** 2);
}

// ── Shared-segment detection ──────────────────────────────────────────────────

/**
 * Find the collinear overlap between two directed segments a1→a2 and b1→b2.
 * Returns null if they are not collinear or do not overlap meaningfully.
 *
 * Returned points p1/p2 are ordered along a1→a2.
 * tAstart/tAend and tBstart/tBend are parametric [0,1] ranges on each edge.
 */
function collinearOverlap(a1, a2, b1, b2, tol) {
  const dax = a2.x - a1.x, day = a2.y - a1.y;
  const lenA = Math.sqrt(dax * dax + day * day);
  if (lenA < 1e-6) return null;

  const uax = dax / lenA, uay = day / lenA; // unit dir of A
  const pax = -uay, pay = uax;              // perpendicular to A

  // Collinearity: b1 and b2 must both lie within `tol` of A's infinite line
  if (Math.abs((b1.x - a1.x) * pax + (b1.y - a1.y) * pay) > tol) return null;
  if (Math.abs((b2.x - a1.x) * pax + (b2.y - a1.y) * pay) > tol) return null;

  // Project b endpoints onto A's axis (signed scalar from a1)
  const tb1 = (b1.x - a1.x) * uax + (b1.y - a1.y) * uay;
  const tb2 = (b2.x - a1.x) * uax + (b2.y - a1.y) * uay;

  // Overlap interval clamped to [0, lenA]
  const lo = Math.max(0, Math.min(tb1, tb2));
  const hi = Math.min(lenA, Math.max(tb1, tb2));
  if (hi - lo < tol) return null;

  const p1 = { x: a1.x + uax * lo, y: a1.y + uay * lo };
  const p2 = { x: a1.x + uax * hi, y: a1.y + uay * hi };

  const tAstart = lo / lenA;
  const tAend   = hi / lenA;

  // Parametric range along B
  const dbx = b2.x - b1.x, dby = b2.y - b1.y;
  const lenB = Math.sqrt(dbx * dbx + dby * dby);
  let tBstart = 0, tBend = 1;
  if (lenB > 1e-6) {
    const ubx = dbx / lenB, uby = dby / lenB;
    const sp1 = Math.max(0, Math.min(lenB, (p1.x - b1.x) * ubx + (p1.y - b1.y) * uby));
    const sp2 = Math.max(0, Math.min(lenB, (p2.x - b1.x) * ubx + (p2.y - b1.y) * uby));
    tBstart = Math.min(sp1, sp2) / lenB;
    tBend   = Math.max(sp1, sp2) / lenB;
  }

  return { p1, p2, tAstart, tAend, tBstart, tBend };
}

/**
 * Detect partition walls between zones on the same floor.
 * Now handles both full-edge matches and partial (T-intersection) overlaps.
 * Used by the 2D canvas for visual coloring (marks the containing edge as partition).
 *
 * @param {Array} zones
 * @returns {Array<{wall1, wall2, zone1, zone2}>}
 */
export function detectPartitions(zones) {
  const partitions = [];
  const TOL = 0.05;

  for (let i = 0; i < zones.length; i++) {
    for (let j = i + 1; j < zones.length; j++) {
      const fp1 = zones[i].footprint, fp2 = zones[j].footprint;
      for (let e1 = 0; e1 < fp1.length; e1++) {
        const a1 = fp1[e1], a2 = fp1[(e1 + 1) % fp1.length];
        for (let e2 = 0; e2 < fp2.length; e2++) {
          const b1 = fp2[e2], b2 = fp2[(e2 + 1) % fp2.length];
          if (collinearOverlap(a1, a2, b1, b2, TOL)) {
            partitions.push({
              wall1: `${zones[i].id}_wall_${e1}`,
              wall2: `${zones[j].id}_wall_${e2}`,
              zone1: zones[i].id,
              zone2: zones[j].id,
            });
          }
        }
      }
    }
  }
  return partitions;
}

/**
 * Find all collinear-overlapping sub-segments between every zone pair.
 * Internal helper used by generateAllSurfaces for precise wall splitting.
 */
function findSharedSegments(zones, tol = 0.05) {
  const result = [];
  for (let i = 0; i < zones.length; i++) {
    for (let j = i + 1; j < zones.length; j++) {
      const fpA = zones[i].footprint, fpB = zones[j].footprint;
      for (let eA = 0; eA < fpA.length; eA++) {
        const a1 = fpA[eA], a2 = fpA[(eA + 1) % fpA.length];
        for (let eB = 0; eB < fpB.length; eB++) {
          const b1 = fpB[eB], b2 = fpB[(eB + 1) % fpB.length];
          const ov = collinearOverlap(a1, a2, b1, b2, tol);
          if (ov) {
            result.push({
              ziA: i, eA, tAstart: ov.tAstart, tAend: ov.tAend,
              ziB: j, eB, tBstart: ov.tBstart, tBend: ov.tBend,
              p1: ov.p1, p2: ov.p2,
            });
          }
        }
      }
    }
  }
  return result;
}

/** Linear interpolation between two 2D points */
function interp2D(p1, p2, t) {
  return { x: p1.x + (p2.x - p1.x) * t, y: p1.y + (p2.y - p1.y) * t };
}

/** Build a single wall surface object from two 2D endpoints */
function makeWallSurface(p1, p2, elevation, height, zoneId, name, bc, adjSurface, edgeIndex) {
  const topZ = elevation + height;
  const az   = computeWallAzimuth(p1, p2);
  return {
    id: `${zoneId}_${name.replace(/[^a-zA-Z0-9_]/g, '_')}`,
    // Canonical per-edge wall id — stable across render/resolver/export and shared by
    // all sub-segments of a partially-shared edge. Matches footprintToSurfaces' id so
    // per-wall zone envelope overrides resolve to the right surface(s). May be undefined
    // for callers that don't pass an edge index (back-compat).
    wallId: edgeIndex != null ? `${zoneId}_wall_${edgeIndex}` : undefined,
    name,
    zone_id: zoneId,
    type: 'wall',
    azimuth: az,
    bin: bin8(az),
    vertices: [
      { x: p1.x, y: p1.y, z: topZ },
      { x: p2.x, y: p2.y, z: topZ },
      { x: p2.x, y: p2.y, z: elevation },
      { x: p1.x, y: p1.y, z: elevation },
    ],
    area: wallArea(p1, p2, height),
    boundary_condition: bc,
    adjacent_surface: adjSurface,
    construction: null,
    openings: [],
  };
}

/**
 * Snap a point to the nearest edge of existing zones.
 * Returns the snapped point and edge info if within tolerance.
 * 
 * @param {{x: number, y: number}} point - Point to snap
 * @param {Array} zones - Existing zones
 * @param {number} tolerance - Snap tolerance in meters (default 0.05 = 5cm)
 * @returns {{ snapped: {x: number, y: number}, edge: Object|null }}
 */
export function snapToEdge(point, zones, tolerance = 0.05) {
  let bestDist = Infinity;
  let bestSnap = point;
  let bestEdge = null;

  for (const zone of zones) {
    const fp = zone.footprint;
    for (let i = 0; i < fp.length; i++) {
      const a = fp[i];
      const b = fp[(i + 1) % fp.length];

      // Project point onto edge
      const snap = projectPointOnSegment(point, a, b);
      const dist = distance2D(point, snap);

      if (dist < tolerance && dist < bestDist) {
        bestDist = dist;
        bestSnap = snap;
        bestEdge = { zoneId: zone.id, edgeIndex: i, a, b };
      }
    }
  }

  return { snapped: bestSnap, edge: bestEdge };
}

/**
 * Snap a value to the nearest grid line
 */
export function snapToGrid(value, gridSize) {
  return Math.round(value / gridSize) * gridSize;
}

/**
 * Project a point onto a line segment, clamped to segment endpoints
 */
function projectPointOnSegment(point, a, b) {
  const dx = b.x - a.x;
  const dy = b.y - a.y;
  const lenSq = dx * dx + dy * dy;

  if (lenSq < 1e-10) return { ...a };

  let t = ((point.x - a.x) * dx + (point.y - a.y) * dy) / lenSq;
  t = Math.max(0, Math.min(1, t));

  return {
    x: a.x + t * dx,
    y: a.y + t * dy,
  };
}

/**
 * Snap each vertex of a new footprint to the nearest point on any existing
 * zone boundary within `tolerance` metres.  Eliminates gaps and misalignments
 * that occur when zones are drawn slightly off from their neighbours.
 *
 * @param {Array<{x,y}>} footprint  - The new zone's vertices to heal
 * @param {Array}        zones      - Existing zones on the same floor
 * @param {number}       tolerance  - Max snap distance in metres (default 0.20 m)
 * @returns {Array<{x,y}>}          - Healed footprint
 */
export function healFootprint(footprint, zones, tolerance = 0.20) {
  if (!zones || zones.length === 0) return footprint;
  return footprint.map((vertex) => {
    let bestDist = tolerance;
    let best = vertex;
    for (const zone of zones) {
      const fp = zone.footprint;
      for (let i = 0; i < fp.length; i++) {
        // Vertex-to-vertex
        const zv = fp[i];
        const dv = distance2D(vertex, zv);
        if (dv < bestDist) { bestDist = dv; best = { x: zv.x, y: zv.y }; }
        // Vertex-to-edge (project onto zone boundary segment)
        const ze = fp[(i + 1) % fp.length];
        const proj = projectPointOnSegment(vertex, zv, ze);
        const de = distance2D(vertex, proj);
        if (de < bestDist) { bestDist = de; best = { x: proj.x, y: proj.y }; }
      }
    }
    return best;
  });
}

/**
 * Get the bounding box of a footprint
 */
export function footprintBounds(footprint) {
  let minX = Infinity, minY = Infinity, maxX = -Infinity, maxY = -Infinity;
  for (const p of footprint) {
    minX = Math.min(minX, p.x);
    minY = Math.min(minY, p.y);
    maxX = Math.max(maxX, p.x);
    maxY = Math.max(maxY, p.y);
  }
  return { minX, minY, maxX, maxY, width: maxX - minX, height: maxY - minY };
}

/**
 * Get the bounding box of all zones on a floor
 */
export function floorBounds(zones) {
  if (!zones.length) return { minX: -20, minY: -20, maxX: 20, maxY: 20, width: 40, height: 40 };
  
  let minX = Infinity, minY = Infinity, maxX = -Infinity, maxY = -Infinity;
  for (const zone of zones) {
    const b = footprintBounds(zone.footprint);
    minX = Math.min(minX, b.minX);
    minY = Math.min(minY, b.minY);
    maxX = Math.max(maxX, b.maxX);
    maxY = Math.max(maxY, b.maxY);
  }
  const padding = 5;
  return {
    minX: minX - padding,
    minY: minY - padding,
    maxX: maxX + padding,
    maxY: maxY + padding,
    width: maxX - minX + padding * 2,
    height: maxY - minY + padding * 2,
  };
}

/**
 * Generate all surfaces for a complete building (all floors, all zones).
 *
 * Each zone edge is split at shared-sub-segment boundaries so that:
 *   - Shared sub-segments (full OR partial overlap) → boundary_condition = 'Surface'
 *     with cross-referenced adjacent_surface names.
 *   - Non-shared portions of the same edge → boundary_condition = 'Outdoors'.
 *
 * @param {Object} building
 * @returns {Array} All surfaces with correct boundary conditions
 */
export function generateAllSurfaces(building) {
  const allSurfaces = [];
  const FULL_TOL = 0.01; // ≤1 % from edge end = "fully shared"

  // BUG-3 FIX: Sort floors by elevation so isTopFloor/isGroundFloor assignments
  // and interzone pairing (_index) are based on physical stacking, not creation order.
  const floors = [...building.floors].sort((a, b) => a.elevation - b.elevation);

  for (let fi = 0; fi < floors.length; fi++) {
    const floor        = floors[fi];
    const isTopFloor   = fi === floors.length - 1;
    const isGroundFloor = fi === 0;
    const zones        = floor.zones;

    // ── Step 1: find every collinear-overlap sub-segment on this floor ──────
    const segs = findSharedSegments(zones);

    // ── Step 2: pre-assign surface names to both sides of every shared seg ──
    // A full-edge partition keeps the standard name (Zone:WallN).
    // A partial partition gets a unique suffix (_PrtS) so names are unambiguous.
    const segNames = segs.map((seg, s) => {
      const zA = zones[seg.ziA], zB = zones[seg.ziB];
      const aFull = seg.tAstart < FULL_TOL && seg.tAend > 1 - FULL_TOL;
      const bFull = seg.tBstart < FULL_TOL && seg.tBend > 1 - FULL_TOL;
      return {
        nameA: aFull ? `${zA.name}:Wall${seg.eA + 1}`
                     : `${zA.name}:Wall${seg.eA + 1}_Prt${s + 1}`,
        nameB: bFull ? `${zB.name}:Wall${seg.eB + 1}`
                     : `${zB.name}:Wall${seg.eB + 1}_Prt${s + 1}`,
      };
    });

    // ── Step 3: build per-edge partition lookup ──────────────────────────────
    // Key: `${zoneIdx}_${edgeIdx}` → sorted list of partition sub-segments
    const edgeMap = new Map();

    for (let s = 0; s < segs.length; s++) {
      const seg = segs[s], names = segNames[s];

      const keyA = `${seg.ziA}_${seg.eA}`;
      if (!edgeMap.has(keyA)) edgeMap.set(keyA, []);
      edgeMap.get(keyA).push({
        tStart: seg.tAstart, tEnd: seg.tAend,
        p1: seg.p1, p2: seg.p2,
        myName: names.nameA, adjName: names.nameB,
      });

      const keyB = `${seg.ziB}_${seg.eB}`;
      if (!edgeMap.has(keyB)) edgeMap.set(keyB, []);
      const fpB = zones[seg.ziB].footprint;
      const b1  = fpB[seg.eB], b2 = fpB[(seg.eB + 1) % fpB.length];
      edgeMap.get(keyB).push({
        tStart: seg.tBstart, tEnd: seg.tBend,
        p1: interp2D(b1, b2, seg.tBstart),
        p2: interp2D(b1, b2, seg.tBend),
        myName: names.nameB, adjName: names.nameA,
      });
    }

    // ── Step 4: generate surfaces zone by zone ───────────────────────────────
    for (let zi = 0; zi < zones.length; zi++) {
      const zone = zones[zi];
      const fp   = zone.footprint;           // raw footprint — keeps all edges for partition detection

      // BUG-1 FIX: skip degenerate zones (< 3 vertices) — EnergyPlus requires ≥ 3.
      if (!fp || fp.length < 3) continue;

      const sfp  = removeCollinearVertices(fp); // simplified — used for floor/ceiling slabs only
      const elev = floor.elevation;
      const ht   = floor.height;

      // BUG-2 FIX: skip zero-height floors — walls and roof would have zero area.
      if (ht <= 0) {
        // Still emit the floor slab so Ground BC is represented, but no walls/ceiling.
        allSurfaces.push({
          id: `${zone.id}_floor`,
          name: `${zone.name}:Floor`,
          zone_id: zone.id, type: 'floor',
          azimuth: null, bin: null,
          vertices: [...sfp].reverse().map((p) => ({ x: p.x, y: p.y, z: elev })),
          area: polygonArea(fp),
          boundary_condition: isGroundFloor ? 'Ground' : 'Surface',
          adjacent_surface: null, construction: null, openings: [],
        });
        continue;
      }

      const walls = [];

      for (let ei = 0; ei < fp.length; ei++) {
        const a1   = fp[ei], a2 = fp[(ei + 1) % fp.length];
        // BUG-1 FIX: skip zero-length edges (duplicate adjacent vertices).
        if (distance2D(a1, a2) < 1e-6) continue;
        const subs = edgeMap.get(`${zi}_${ei}`);

        if (!subs || subs.length === 0) {
          // Entirely external wall
          walls.push(makeWallSurface(
            a1, a2, elev, ht, zone.id,
            `${zone.name}:Wall${ei + 1}`, 'Outdoors', null, ei,
          ));
        } else {
          // One or more partition sub-segments — split the edge
          const sorted = [...subs].sort((x, y) => x.tStart - y.tStart);
          let extIdx = 0, cursor = 0;

          for (const sub of sorted) {
            if (sub.tStart - cursor > FULL_TOL) {
              walls.push(makeWallSurface(
                interp2D(a1, a2, cursor), interp2D(a1, a2, sub.tStart),
                elev, ht, zone.id,
                `${zone.name}:Wall${ei + 1}_Ext${++extIdx}`, 'Outdoors', null, ei,
              ));
            }
            walls.push(makeWallSurface(
              sub.p1, sub.p2, elev, ht, zone.id,
              sub.myName, 'Surface', sub.adjName, ei,
            ));
            cursor = sub.tEnd;
          }

          if (1 - cursor > FULL_TOL) {
            walls.push(makeWallSurface(
              interp2D(a1, a2, cursor), a2,
              elev, ht, zone.id,
              `${zone.name}:Wall${ei + 1}_Ext${++extIdx}`, 'Outdoors', null, ei,
            ));
          }
        }
      }

      // Floor slab — outward normal must point DOWN (tilt ≈ 180°).
      // Use sfp (collinear-vertices removed) so our IDF vertex count matches
      // what EnergyPlus produces after its own collinearity filter — this keeps
      // the pairInterzoneFloors vertex-count check consistent with EnergyPlus.
      allSurfaces.push({
        id: `${zone.id}_floor`,
        name: `${zone.name}:Floor`,
        zone_id: zone.id, type: 'floor',
        azimuth: null, bin: null,
        vertices: [...sfp].reverse().map((p) => ({ x: p.x, y: p.y, z: elev })),
        area: polygonArea(fp),
        boundary_condition: isGroundFloor ? 'Ground' : 'Surface',
        adjacent_surface: null, construction: null, openings: [],
      });

      // Ceiling / roof — outward normal must point UP (tilt ≈ 0°).
      allSurfaces.push({
        id: `${zone.id}_ceiling`,
        name: isTopFloor ? `${zone.name}:Roof` : `${zone.name}:Ceiling`,
        zone_id: zone.id, type: isTopFloor ? 'roof' : 'ceiling',
        azimuth: null, bin: null,
        vertices: sfp.map((p) => ({ x: p.x, y: p.y, z: elev + ht })),
        area: polygonArea(fp),
        boundary_condition: isTopFloor ? 'Outdoors' : 'Surface',
        adjacent_surface: null, construction: null, openings: [],
      });

      allSurfaces.push(...walls);
    }
  }

  return allSurfaces;
}

// ── Zone splitting ────────────────────────────────────────────────────────────

/**
 * Project a point onto a polygon boundary and return the closest hit.
 * Returns { edgeIndex, t, point } or null if nothing within tolerance.
 */
function snapToBoundaryPoint(pt, polygon, tolerance) {
  let bestDist = tolerance;
  let best = null;
  for (let i = 0; i < polygon.length; i++) {
    const a = polygon[i];
    const b = polygon[(i + 1) % polygon.length];
    const snap = projectPointOnSegment(pt, a, b);
    const dist = distance2D(pt, snap);
    if (dist < bestDist) {
      bestDist = dist;
      const edgeLen = distance2D(a, b);
      const t = edgeLen > 0.001 ? distance2D(a, snap) / edgeLen : 0;
      best = { edgeIndex: i, t, point: snap };
    }
  }
  return best;
}

/**
 * Normalise a boundary hit so t ≈ 1 is expressed as t = 0 on the next edge.
 * This eliminates the duplicate-vertex bug when a cut starts/ends at a corner.
 */
function normaliseSnap(snap, n, threshold = 0.01) {
  let { edgeIndex, t, point } = snap;
  if (t > 1 - threshold) { edgeIndex = (edgeIndex + 1) % n; t = 0; }
  else if (t < threshold) { t = 0; }
  return { edgeIndex, t, point };
}

/**
 * Walk forward (CW in canvas) from fromSnap to toSnap along the polygon,
 * collecting intermediate corner vertices.  Uses normalised arc positions
 * to avoid duplicating the vertex when a snap lands exactly at a corner.
 *
 * Arc position of a snap = edgeIndex + t  (a float in [0, n))
 */
function buildHalf(polygon, fromSnap, toSnap) {
  const n = polygon.length;
  const from = normaliseSnap(fromSnap, n);
  const to   = normaliseSnap(toSnap,   n);

  const fromArc = from.edgeIndex + from.t;
  const toArc   = to.edgeIndex   + to.t;
  // Ensure we walk forward; wrap if needed
  const targetArc = toArc <= fromArc + 1e-9 ? toArc + n : toArc;

  const result = [{ x: fromSnap.point.x, y: fromSnap.point.y }];

  // Include every polygon vertex whose arc position is strictly between
  // fromArc and targetArc (avoids duplicating the from/to points)
  const firstVi = Math.floor(fromArc) + 1;
  for (let vi = firstVi; vi < targetArc - 1e-9; vi++) {
    result.push({ x: polygon[vi % n].x, y: polygon[vi % n].y });
  }

  result.push({ x: toSnap.point.x, y: toSnap.point.y });
  return result;
}

/**
 * Split a zone polygon along a cut path.
 * cutPath must start and end on the polygon boundary.
 *
 * @param {Array<{x,y}>} polygon  - The zone footprint to split
 * @param {Array<{x,y}>} cutPath  - Drawn path [start, ...interior, end]
 * @param {number}       tolerance
 * @returns {[Array<{x,y}>, Array<{x,y}>] | null}
 */
export function splitPolygonByPath(polygon, cutPath, tolerance = 0.3) {
  if (cutPath.length < 2) return null;

  const fromSnap = snapToBoundaryPoint(cutPath[0], polygon, tolerance);
  if (!fromSnap) return null;

  const toSnap = snapToBoundaryPoint(cutPath[cutPath.length - 1], polygon, tolerance);
  if (!toSnap) return null;

  // Points must be meaningfully distinct on the boundary
  if (distance2D(fromSnap.point, toSnap.point) < tolerance * 0.5) return null;

  const interior = cutPath.slice(1, -1);

  // Half 1: walk boundary from → to, close back through interior reversed
  const half1 = buildHalf(polygon, fromSnap, toSnap);
  for (let i = interior.length - 1; i >= 0; i--) {
    half1.push({ x: interior[i].x, y: interior[i].y });
  }

  // Half 2: walk boundary to → from, close back through interior forward
  const half2 = buildHalf(polygon, toSnap, fromSnap);
  for (const pt of interior) {
    half2.push({ x: pt.x, y: pt.y });
  }

  if (half1.length < 3 || half2.length < 3) return null;
  return [half1, half2];
}

/**
 * Point-in-polygon test (ray casting). Returns true if pt is strictly inside polygon.
 * Points exactly on the boundary may return true or false.
 */
export function isPointInPolygon(pt, polygon) {
  let inside = false;
  const n = polygon.length;
  let j = n - 1;
  for (let i = 0; i < n; i++) {
    const xi = polygon[i].x, yi = polygon[i].y;
    const xj = polygon[j].x, yj = polygon[j].y;
    if ((yi > pt.y) !== (yj > pt.y) &&
        pt.x < (xj - xi) * (pt.y - yi) / (yj - yi) + xi) {
      inside = !inside;
    }
    j = i;
  }
  return inside;
}

/** Distance from point (px,py) to segment (ax,ay)-(bx,by). */
function distToSegment(px, py, ax, ay, bx, by) {
  const dx = bx - ax, dy = by - ay;
  const len2 = dx * dx + dy * dy;
  if (len2 < 1e-12) return Math.hypot(px - ax, py - ay);
  let t = ((px - ax) * dx + (py - ay) * dy) / len2;
  t = Math.max(0, Math.min(1, t));
  return Math.hypot(px - (ax + t * dx), py - (ay + t * dy));
}

/**
 * Visual center (pole of inaccessibility) — the interior point of a polygon
 * farthest from any edge. Use this for label placement instead of the vertex
 * average, which can fall OUTSIDE concave / L-shaped polygons (placing a zone's
 * label visually inside a different zone). Simplified, dependency-free grid
 * refinement of the polylabel algorithm.
 *
 * @param {Array<{x,y}>} polygon
 * @returns {{x:number, y:number}}
 */
export function visualCenter(polygon) {
  const n = polygon?.length || 0;
  if (n < 3) {
    return {
      x: n ? polygon.reduce((s, p) => s + p.x, 0) / n : 0,
      y: n ? polygon.reduce((s, p) => s + p.y, 0) / n : 0,
    };
  }

  const cx = polygon.reduce((s, p) => s + p.x, 0) / n;
  const cy = polygon.reduce((s, p) => s + p.y, 0) / n;
  const b  = footprintBounds(polygon);
  const minDim = Math.min(b.width, b.height) || 1;

  // Signed clearance: distance to nearest edge, positive only when inside.
  const clearance = (px, py) => {
    let dmin = Infinity;
    for (let i = 0, j = n - 1; i < n; j = i++) {
      dmin = Math.min(dmin, distToSegment(px, py, polygon[j].x, polygon[j].y, polygon[i].x, polygon[i].y));
    }
    return isPointInPolygon({ x: px, y: py }, polygon) ? dmin : -dmin;
  };

  // Fast path: for convex / rectangular zones the centroid sits well inside.
  if (clearance(cx, cy) > minDim * 0.15) return { x: cx, y: cy };

  // Coarse grid search, then one refinement pass around the best cell.
  let best = { x: cx, y: cy };
  let bestD = clearance(cx, cy);
  const search = (x0, y0, x1, y1, steps) => {
    const sx = (x1 - x0) / steps, sy = (y1 - y0) / steps;
    for (let i = 0; i <= steps; i++) {
      for (let k = 0; k <= steps; k++) {
        const px = x0 + sx * i, py = y0 + sy * k;
        const d = clearance(px, py);
        if (d > bestD) { bestD = d; best = { x: px, y: py }; }
      }
    }
  };
  search(b.minX, b.minY, b.maxX, b.maxY, 16);
  const cw = b.width / 16, ch = b.height / 16;
  search(best.x - cw, best.y - ch, best.x + cw, best.y + ch, 8);
  return best;
}

/**
 * Returns true if two zone footprints genuinely overlap in area.
 *
 * Uses centroid containment only — NOT vertex containment — because adjacent zones
 * share boundary vertices and isPointInPolygon is undefined on boundary points,
 * causing false positives for legitimately adjacent (non-overlapping) rooms.
 *
 * Centroid containment reliably detects:
 *   • One zone completely inside another (centroid is inside)
 *   • Zones that overlap by more than ~50% of their area (centroid crosses over)
 * It correctly ignores zones that share only a boundary edge (centroids stay
 * in their own regions).
 */
export function polygonsOverlap(fp1, fp2) {
  const c1 = {
    x: fp1.reduce((s, p) => s + p.x, 0) / fp1.length,
    y: fp1.reduce((s, p) => s + p.y, 0) / fp1.length,
  };
  if (isPointInPolygon(c1, fp2)) return true;

  const c2 = {
    x: fp2.reduce((s, p) => s + p.x, 0) / fp2.length,
    y: fp2.reduce((s, p) => s + p.y, 0) / fp2.length,
  };
  if (isPointInPolygon(c2, fp1)) return true;

  return false;
}

// ── Segment intersection ──────────────────────────────────────────────────────

/**
 * Find all intersections of line segment p1→p2 with a polygon's edges.
 * Returns array of {x, y, t} sorted by t (parameter along p1→p2).
 */
export function segmentPolygonIntersections(p1, p2, polygon) {
  const hits = [];
  for (let i = 0; i < polygon.length; i++) {
    const a = polygon[i];
    const b = polygon[(i + 1) % polygon.length];
    const hit = segmentSegmentIntersect(p1, p2, a, b);
    if (hit) hits.push(hit);
  }
  return hits.sort((a, b) => a.t - b.t);
}

function segmentSegmentIntersect(p1, p2, p3, p4) {
  const d1x = p2.x - p1.x, d1y = p2.y - p1.y;
  const d2x = p4.x - p3.x, d2y = p4.y - p3.y;
  const cross = d1x * d2y - d1y * d2x;
  if (Math.abs(cross) < 1e-10) return null; // parallel / collinear
  const dx = p3.x - p1.x, dy = p3.y - p1.y;
  const t = (dx * d2y - dy * d2x) / cross;
  const u = (dx * d1y - dy * d1x) / cross;
  if (t >= -1e-6 && t <= 1 + 1e-6 && u >= -1e-6 && u <= 1 + 1e-6) {
    return { x: p1.x + t * d1x, y: p1.y + t * d1y, t };
  }
  return null;
}

/**
 * Get compass direction color CSS variable name
 */
export function directionColor(bin) {
  const map = {
    'N': 'var(--dir-n)',
    'NE': 'var(--dir-ne)',
    'E': 'var(--dir-e)',
    'SE': 'var(--dir-se)',
    'S': 'var(--dir-s)',
    'SW': 'var(--dir-sw)',
    'W': 'var(--dir-w)',
    'NW': 'var(--dir-nw)',
  };
  return map[bin] || 'var(--text-tertiary)';
}

/**
 * Get hex color for compass direction (for Three.js materials)
 */
export function directionColorHex(bin) {
  const map = {
    'N': '#60a5fa',
    'NE': '#818cf8',
    'E': '#f59e0b',
    'SE': '#f97316',
    'S': '#f43f5e',
    'SW': '#ec4899',
    'W': '#10b981',
    'NW': '#06b6d4',
  };
  return map[bin] || '#5c6b8a';
}

// ── Footprint winding ─────────────────────────────────────────────────────────

/**
 * Twice the signed area of a footprint in canvas coordinates (y down). POSITIVE when the vertices run
 * CLOCKWISE on screen — the winding every wall builder relies on: computeWallAzimuth takes the right-hand
 * perpendicular of p1→p2 as the outside. An anticlockwise footprint puts the outside of every wall INSIDE
 * its zone — in the app's façade directions, the 3D view, RETV/EPF and the IDF alike.
 */
export function footprintSignedArea2(fp) {
  if (!Array.isArray(fp)) return 0;
  let a = 0;
  for (let i = 0; i < fp.length; i++) {
    const p = fp[i], q = fp[(i + 1) % fp.length];
    if (!Number.isFinite(p?.x) || !Number.isFinite(p?.y) || !Number.isFinite(q?.x) || !Number.isFinite(q?.y)) return 0;
    a += p.x * q.y - q.x * p.y;
  }
  return a;
}

/** Does this footprint run clockwise on screen (the winding the walls need)? */
export function isClockwiseFootprint(fp) {
  return footprintSignedArea2(fp) > 0;
}

/** The footprint running clockwise on screen — the same array when it already does. */
export function clockwiseFootprint(fp) {
  if (!Array.isArray(fp) || fp.length < 3) return fp;
  return footprintSignedArea2(fp) < 0 ? [...fp].reverse() : fp;
}

/**
 * The zone with its footprint running clockwise and every per-wall entry still on ITS physical wall.
 * Reversing n vertices turns edge i (p_i → p_i+1) into edge (n − 2 − i) mod n, so the
 * `${zoneId}_wall_${i}` keys of wallOverrides and wallOpenings are renumbered to match.
 * Returns the very same object when the footprint already runs clockwise.
 */
export function normalizeZoneWinding(zone) {
  const fp = zone?.footprint;
  if (!Array.isArray(fp) || fp.length < 3 || footprintSignedArea2(fp) >= 0) return zone;
  const n = fp.length;
  const prefix = `${zone.id}_wall_`;
  const remap = (map) => {
    const out = {};
    for (const [key, value] of Object.entries(map)) {
      const rest = key.startsWith(prefix) ? key.slice(prefix.length) : null;
      const i = rest != null && /^\d+$/.test(rest) ? Number(rest) : null;
      out[i != null && i < n ? `${prefix}${(((n - 2 - i) % n) + n) % n}` : key] = value;
    }
    return out;
  };
  const next = { ...zone, footprint: [...fp].reverse() };
  if (zone.wallOverrides && typeof zone.wallOverrides === 'object') next.wallOverrides = remap(zone.wallOverrides);
  if (zone.wallOpenings && typeof zone.wallOpenings === 'object') next.wallOpenings = remap(zone.wallOpenings);
  return next;
}

/** normalizeZoneWinding over every zone of a building. Untouched floors — and the building — keep their identity. */
export function normalizeBuildingWinding(building) {
  if (!building || !Array.isArray(building.floors)) return building;
  let changed = false;
  const floors = building.floors.map((f) => {
    if (!f || !Array.isArray(f.zones)) return f;
    let floorChanged = false;
    const zones = f.zones.map((z) => {
      const nz = normalizeZoneWinding(z);
      if (nz !== z) floorChanged = true;
      return nz;
    });
    if (!floorChanged) return f;
    changed = true;
    return { ...f, zones };
  });
  return changed ? { ...building, floors } : building;
}
